import { Component, ElementRef, ViewChild, afterNextRender, OnDestroy, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { isAuth } from './auth';
import { WwwwwwComponent } from "./iniciofront";

@Component({
  selector: 'app-www',
  standalone: true,
  imports: [WwwwwwComponent],
  template: `
    <div class="landing-container">
      <canvas #canvasRef></canvas>
      
      <div class="footer-overlay">
        <app-wwwwww></app-wwwwww>
      </div>
    </div>
  `,
  styles: [`
    .landing-container {
      position: relative;
      width: 100%;
      min-height: 100vh;
      overflow-x: hidden;
      background: #020617;
      font-family: system-ui, -apple-system, sans-serif;
    }
    canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: block;
      z-index: 1;
    }
    .footer-overlay {
      position: absolute;
      top: 53.5%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      z-index: 10;
      pointer-events: auto;
    }
  `]
})
export class Wwww implements OnDestroy {
  @ViewChild('canvasRef', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  auth: any = isAuth();

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private loadedModel?: THREE.Object3D;
  private pmremGenerator?: THREE.PMREMGenerator;

  private animationFrameId = 0;
  private ngZone = inject(NgZone);
  private router = inject(Router);

  private mouseX = 0;
  private mouseY = 0;
  private targetX = 0;
  private targetY = 0;

  private boundOnResize = this.onWindowResize.bind(this);
  private boundOnMouseMove = this.onMouseMove.bind(this);

  constructor() {
    afterNextRender(() => {
      this.initScene();
    });
  }

  private initScene() {
    const canvas = this.canvasRef.nativeElement;
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    this.camera.position.z = 5;

    // 1. Configuración avanzada del Renderer
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // 2. Entorno de iluminación realista (PMREM + RoomEnvironment)
    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = this.pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;

    // Luces de apoyo complementarias
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
    this.scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0x38bdf8, 1.0);
    pointLight.position.set(5, 5, 5);
    this.scene.add(pointLight);

    const pointLight2 = new THREE.PointLight(0x818cf8, 3.0);
    pointLight2.position.set(-5, -5, 5);
    this.scene.add(pointLight2);

    // 3. Carga del modelo GLB con materiales transparentes / efecto vidrio
    const loader = new GLTFLoader();
    loader.load(
      'new.glb',
      (gltf) => {
        this.loadedModel = gltf.scene;
        this.loadedModel.scale.set(8, 8, 8);

        this.loadedModel.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            if (mesh.material) {
              const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
              
              materials.forEach((mat: any) => {
                if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial) {
                  
                  // --- CONFIGURACIÓN DE TRANSPARENCIA Y VIDRIO ---
                  mat.transparent = true;
                  mat.opacity = 0.3; // Ajusta la opacidad global (0 = invisible, 1 = opaco)
                  
                  if (mesh.name.toLowerCase().includes('accent')) {
                    mat.color.set('#f59e0b');
                  } else {
                    mat.color.set('#251c08');
                  }

                  // Propiedades avanzadas para simular vidrio translúcido si usa MeshPhysicalMaterial
                  if (mat.isMeshPhysicalMaterial) {
                    mat.transmission = 0.8; // Permite que la luz lo atraviese como vidrio
                    mat.roughness = 0.15;   // Superficie muy lisa y pulida
                    mat.metalness = 0.1;    // Menos metálico para que funcione la refracción
                    mat.ior = 1.5;          // Índice de refracción del vidrio
                  } else {
                    mat.roughness = 0.2;
                    mat.metalness = 0.4;
                  }

                  mat.envMapIntensity = 1.8;
                  if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace;
                  mat.needsUpdate = true;
                }
              });
            }
          }
        });

        this.scene.add(this.loadedModel);
      },
      undefined,
      (error) => {
        console.error('Ocurrió un error al cargar el modelo GLB:', error);
      }
    );

    // Bucle de animación fuera de Angular
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.boundOnResize);
      window.addEventListener('mousemove', this.boundOnMouseMove);

      const animate = () => {
        this.animationFrameId = requestAnimationFrame(animate);

        this.targetX += (this.mouseX - this.targetX) * 0.05;
        this.targetY += (this.mouseY - this.targetY) * 0.05;

        if (this.loadedModel) {
          this.loadedModel.rotation.x = this.targetY * 0.5 + Date.now() * 0.0005;
          this.loadedModel.rotation.y = this.targetX * 0.5 + Date.now() * 0.0008;
        }

        this.renderer.render(this.scene, this.camera);
      };
      animate();
    });
  }

  private onMouseMove(event: MouseEvent) {
    this.mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  private onWindowResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  ngOnDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    window.removeEventListener('resize', this.boundOnResize);
    window.removeEventListener('mousemove', this.boundOnMouseMove);

    if (this.pmremGenerator) {
      this.pmremGenerator.dispose();
    }

    if (this.loadedModel) {
      this.scene.remove(this.loadedModel);
      this.loadedModel.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          if (mesh.geometry) mesh.geometry.dispose();
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((mat) => mat.dispose());
          } else if (mesh.material) {
            mesh.material.dispose();
          }
        }
      });
    }

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  }
}